import { gsap } from "gsap";
import { Container, Graphics, Sprite, Texture } from "pixi.js";

import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { symbolConfig } from "../../../UCConfig";
import { UCSpineNames } from "../../../assetsLoader/UCAssetsNames";
import {
    UCHighSymbolNames,
    UCLowSymbolNames,
    UCSymbolNames,
    UCSymbolSize,
} from "../../../types/UCEnums";
import { UCSymbol } from "./UCSymbol";
import { symbolsResizeConfig } from "./UCSymbolsResizeConfig";

export class UCMysterySymbol extends UCSymbol {
    public newSymbolClones: AbstractSpine[] = [];
    protected texturesCloneContainer: Container;
    protected separatorsContainer: Container;
    protected cloneWinFrames: AbstractSpine[] = [];

    public multiplier: number = 0;

    constructor() {
        super(UCSymbolNames.MYSTERY);
    }

    protected override generateSymbol() {
        super.generateSymbol();
        this.removeChild(this.mainTextureSprite);
    }

    protected init(): void {
        super.init();

        this.texturesCloneContainer = new Container();
        this.texturesCloneContainer.name = `TexturesCloneContainer`;
        this.addChild(this.texturesCloneContainer);

        this.separatorsContainer = new Container();
        this.separatorsContainer.name = `SeparatorsContainer`;
        this.addChild(this.separatorsContainer);

        this.mainSpine = new AbstractSpine(UCSpineNames.MYSTERY);

        this.mainSpine.x =
            symbolConfig.symbolWidth / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
        this.mainSpine.y =
            symbolConfig.symbolHeight / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
        this.addChild(this.mainSpine);

        this.removeChild(this.mainTextureSprite);
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();
        let newHeight: number = (symbolConfig.symbolHeight * 2) / this.multiplier;
        if (this.multiplier === 2) {
            newHeight += 4;
        } else if (this.multiplier === 3) {
            newHeight += 3;
        }

        this.cloneWinFrames = [];
        this.newSymbolClones.forEach((clone) => {
            tl.add(
                clone.getSpineTween(`Win`, false, () => {
                    clone.tint = 0xffffff;
                }),
                0
            );

            if (
                this.size < UCSymbolSize.X075 ||
                Object.values(UCLowSymbolNames).includes(
                    this.revealedSymbolName as UCLowSymbolNames
                ) ||
                Object.values(UCHighSymbolNames).includes(
                    this.revealedSymbolName as UCHighSymbolNames
                )
            ) {
                const winFrameSpine = new AbstractSpine(UCSpineNames.WIN_FRAME);
                const winFrameAnimation = winFrameSpine.getSpineTween(`Win`, false, () => {
                    winFrameSpine.height = newHeight;

                    winFrameSpine.x = symbolConfig.symbolWidth / 2;
                    winFrameSpine.y = (clone.mask! as Sprite).y + 2;
                    // winFrameSpine.y = newHeight * index + newHeight / 2;
                    if (this.multiplier === 2) {
                        winFrameSpine.y += 4;
                    }

                    this.texturesCloneContainer.addChildAt(winFrameSpine, 0);
                    this.cloneWinFrames.push(winFrameSpine);
                });
                tl.add(winFrameAnimation, 0);
            }
        });

        return tl;
    }

    public override getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();

        this.newSymbolClones.forEach((clone) => {
            tl.add(
                gsap.to(clone, {
                    pixi: { tint: 0x555555 },
                    duration: 0.1,
                }),
                0
            );
        });

        return tl; //super.getWinAnimation();
    }

    public removeTint(): void {
        this.newSymbolClones.forEach((clone) => {
            clone.tint = 0xffffff;
        });
    }

    public replaceSymbolAnimation(
        newSymbolName: UCSymbolNames
    ): gsap.core.Tween | gsap.core.Timeline {
        this.revealedSymbolName = newSymbolName;

        const timeline = gsap.timeline({});
        const animationDuration = 0.2;

        const newHeight: number = this.symbolHeight * this.size;
        const dividedHeight: number = newHeight / this.multiplier;

        for (let i = 0; i < this.multiplier; i++) {
            let scaleConfig;
            switch (this.multiplier) {
                case 4:
                    scaleConfig = symbolsResizeConfig.get(newSymbolName)!.x025!;
                    break;

                case 3:
                    scaleConfig = symbolsResizeConfig.get(newSymbolName)!.x075!;

                    break;

                default:
                    scaleConfig = symbolsResizeConfig.get(newSymbolName)!.default!;

                    break;
            }

            let newY = (i + 0.5) * dividedHeight;

            const newSymbolTimeline = gsap.timeline();

            const newSymbolClone = new AbstractSpine(`spine/${newSymbolName}.json`); //generateScaledSprite(symbolName);
            newSymbolClone.name = `${newSymbolName}_clone_${i}`;
            // newSymbolClone.anchor.set(0.5);
            newSymbolClone.alpha = 0;
            newSymbolClone.x = symbolConfig.symbolWidth / 2 + scaleConfig.offsetX; // - 40;
            newSymbolClone.y = newY + scaleConfig.offsetY + 20; // - 40;
            newSymbolClone.scale.set(0.4);

            this.texturesCloneContainer.addChild(newSymbolClone);
            this.newSymbolClones.push(newSymbolClone);

            const showNewSymbolAnimation = gsap.to(newSymbolClone, {
                duration: animationDuration,
                alpha: 1,
                ease: `power1.in`,
                pixi: {
                    scale: scaleConfig.scale,
                },
                y: newY + scaleConfig.offsetY,
            });

            newSymbolTimeline.add(showNewSymbolAnimation, 0);

            if (i < this.multiplier - 1) {
                const separator = new Sprite(Texture.from(`separator`));
                separator.x = -2;
                separator.y = (i + 1) * dividedHeight - separator.height / 2;
                separator.alpha = 0;
                this.separatorsContainer.addChild(separator);

                const separatorAnimation = gsap.to(separator, {
                    duration: animationDuration / 4,
                    alpha: 1,
                });

                newSymbolTimeline.add(separatorAnimation, 0);
            }

            const newSymbolCloneMask = new Graphics();
            newSymbolCloneMask.beginFill(Math.random() * 0xffffff, 1); // ? maybe remove math random
            newSymbolCloneMask.drawRect(0, 0, symbolConfig.symbolWidth, dividedHeight);
            newSymbolCloneMask.endFill();
            newSymbolCloneMask.pivot.set(
                newSymbolCloneMask.width / 2,
                newSymbolCloneMask.height / 2
            );
            newSymbolCloneMask.x = symbolConfig.symbolWidth / 2;
            newSymbolCloneMask.y = (i + 0.5) * dividedHeight;

            this.texturesCloneContainer.addChild(newSymbolCloneMask);
            newSymbolClone.mask = newSymbolCloneMask;

            newSymbolTimeline.timeScale(2);
            timeline.add(newSymbolTimeline, 0.02 * (this.multiplier - 1 - i));
        }

        this.addChild(this.separatorsContainer);

        return timeline;
    }

    public override getResizeAnimation(): gsap.core.Tween | gsap.core.Timeline {
        this.mainSpineMask = new Sprite(Texture.WHITE);
        // this.mainSpineMask.alpha = 0.5;
        this.mainSpineMask.name = `textureMask`;
        this.mainSpineMask.width = symbolConfig.symbolWidth;
        this.mainSpineMask.height = this.symbolHeight;
        this.mainSpineMask.anchor.set(0.5);
        this.mainSpineMask.x = symbolConfig.symbolWidth / 2;
        this.mainSpineMask.y = symbolConfig.symbolHeight / 2;
        this.addChild(this.mainSpineMask);
        // this.mainSpineMask.alpha = 1;
        this.mainSpine.mask = this.mainSpineMask;

        this.removeChild(this.mainTextureSprite);

        const timeline = gsap.timeline({
            onStart: () => {
                this.topSeparator.alpha = 0;

                if (this.positionInArray === 1) {
                    this.topSeparator.y = 4.5;
                }
                this.addChild(this.topSeparator);

                if (this.positionInArray === 5) {
                    this.bottomSeparator.alpha = 0;
                    this.bottomSeparator.x = this.topSeparator.x;
                    this.addChild(this.bottomSeparator);
                }
            },
            onComplete: () => {
                this.removeChild(this.mainSpine);
                this.removeChild(this.mainSpineMask);
                this.mainSpine.mask = null;
            },
        });

        const animationName = this.positionInArray === 1 ? `Win_extend_down` : `Win_extend_up`;
        const growAnimation = this.mainSpine.getSpineTween(animationName);
        const animationDuration = 0.2;
        timeline.add(growAnimation, 0);

        if (this.positionInArray !== 1) {
            const moveMainSpineAnimation = gsap.to(this.mainSpine, {
                duration: animationDuration,
                y: 222,
            });

            timeline.add(moveMainSpineAnimation, 0);
        }

        const maskAnimation: gsap.core.Tween = gsap.to(this.mainSpineMask, {
            height: this.symbolHeight * this.size,
            duration: animationDuration,
            y: (this.symbolHeight * this.size) / 2 + 1,
            onUpdate: () => {
                this.bottomSeparator.y = this.mainSpineMask.y + this.mainSpineMask.height / 2;

                if (this.positionInArray === 5) {
                    this.bottomSeparator.y -= 6 * maskAnimation.progress();
                }
            },
        });

        const separatorsAnimation = gsap.to(this.topSeparator, {
            alpha: 1,
            duration: animationDuration,
            onUpdate: () => {
                this.bottomSeparator.alpha = this.topSeparator.alpha;
            },
        });

        timeline.add(maskAnimation, 0);
        timeline.add(separatorsAnimation, 0);

        return timeline;
    }

    public resetOnStartSpin(): void {
        super.resetOnStartSpin();

        this.removeChild(this.mainTextureSprite); // ! vezi ca nu trebuie facut remove daca nu s-a activat

        this.cloneWinFrames.forEach((winFrame) => {
            this.texturesCloneContainer.removeChild(winFrame);
        });
    }

    public override reset(): void {
        super.reset();

        this.texturesCloneContainer.removeChildren();
        this.separatorsContainer.removeChildren();

        this.newSymbolClones = [];

        this.mainSpine.reset();

        this.mainSpine.scale.set(0.5);
        this.mainSpine.x =
            symbolConfig.symbolWidth / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
        this.mainSpine.y =
            symbolConfig.symbolHeight / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
        this.addChild(this.mainSpine);

        this.removeChild(this.mainTextureSprite);
    }

    protected override resetMainSpine() {
        if (this.mainSpine !== undefined && this.name !== UCSymbolNames.MYSTERY) {
            this.mainSpine.reset();

            this.mainSpine.mask = null;
            this.mainSpine.scale.set(0.5);
            this.mainSpine.x =
                symbolConfig.symbolWidth / 2 +
                symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
            this.mainSpine.y =
                symbolConfig.symbolHeight / 2 +
                symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
            this.addChild(this.mainSpine);
        }
    }
}
